<template> </template>

<script setup lang="ts">
import { datadogLogs } from "@datadog/browser-logs";
import { computed, onMounted, ref, watch } from "vue";
import { useRoute } from "vue-router";
import { useStore } from "vuex";
import { hasAiAdvisingChatbotEnabled } from "@/composables/authorization";
import { API_ENTITY_INFORMATION, CONSTANT_KEYS, LAST_AUTH_STATUS } from "@/constants";
import { useGetEntitySupplementById } from "@/providers/hostInstitutions/useGetEntitySupplementById";
import { resolveOktaAccessToken } from "@/util/auth";
import {
  ALLOWED_BOTPRESS_ROUTE_NAMES,
  ATLO_URL_FLAG,
  BOTPRESS_ACTIONS,
  BOTPRESS_CLIENT_EVENTS,
  BOTPRESS_FAB_ROOT_SELECTOR,
  CALENDLY_HOSTNAME,
} from "./constants";

const LAST_AUTH_STATE_KEY = CONSTANT_KEYS.LAST_AUTH_STATE;

const store = useStore();
const route = useRoute();

const domainEntityId = computed(() => store.getters.getDomainEntityId);
const { data: entitySupplement } = useGetEntitySupplementById(domainEntityId);

const isBotpressEnabledForEntity = computed(() => {
  if (domainEntityId.value !== API_ENTITY_INFORMATION.id) {
    return !!(entitySupplement.value?.ai_advising_enabled && hasAiAdvisingChatbotEnabled.value);
  }
  return !!hasAiAdvisingChatbotEnabled.value;
});

const ALLOWED_ROUTE_NAME_SET = new Set<string>(ALLOWED_BOTPRESS_ROUTE_NAMES);
const isRouteAllowedForBotpress = computed(
  () => typeof route.name === "string" && ALLOWED_ROUTE_NAME_SET.has(route.name),
);
const shouldLoadBotpress = computed(() => isBotpressEnabledForEntity.value && isRouteAllowedForBotpress.value);
const botpressInitialized = ref(false);

const CONVERSATION_GREETED_STORAGE_KEY = CONSTANT_KEYS.BOTPRESS_CONVERSATION_GREETED;

const readConversationGreetedFlag = (): boolean => {
  try {
    return localStorage.getItem(CONVERSATION_GREETED_STORAGE_KEY) === "true";
  } catch (error) {
    datadogLogs.logger.error("Failed to read Botpress conversation-greeted flag", { error });
    return false;
  }
};

const hasGreetedCurrentConversation = ref(readConversationGreetedFlag());

const markConversationGreeted = () => {
  hasGreetedCurrentConversation.value = true;
  try {
    localStorage.setItem(CONVERSATION_GREETED_STORAGE_KEY, "true");
  } catch (error) {
    datadogLogs.logger.error("Failed to persist Botpress conversation-greeted flag", { error });
  }
};

const clearConversationGreeted = () => {
  hasGreetedCurrentConversation.value = false;
  try {
    localStorage.removeItem(CONVERSATION_GREETED_STORAGE_KEY);
  } catch (error) {
    datadogLogs.logger.error("Failed to clear Botpress conversation-greeted flag", { error });
  }
};

const isWidgetOpen = ref(false);

const currentUser = computed(() => store.getters.getCurrentUser);
const isLoggedIn = computed(() => currentUser.value && Object.keys(currentUser.value).length > 0);

const getAuthState = () => (isLoggedIn.value ? LAST_AUTH_STATUS.LOGGED_IN : LAST_AUTH_STATUS.ANONYMOUS);

const syncBotpressAuthState = () => {
  try {
    sessionStorage.setItem(LAST_AUTH_STATE_KEY, getAuthState());
  } catch (error) {
    datadogLogs.logger.error("Failed to sync Botpress auth state", { error });
  }
};

// Detect auth transition when webchat becomes ready (e.g. user came from login page).
// sessionStorage holds the previous auth state so we can reset Botpress if it changed.
const checkAuthTransition = async () => {
  const prev = sessionStorage.getItem(LAST_AUTH_STATE_KEY);
  const current = getAuthState();
  if (prev && prev !== current) {
    window.setTimeout(() => {
      void resetBotpressSession();
    }, 500);
  }
  syncBotpressAuthState();
};

const authToken = computed(() => resolveOktaAccessToken(currentUser.value));
const anonToken = computed(() => store.getters.getAnonToken);

const botpressUserData = computed(() => {
  return {
    authToken: authToken.value || anonToken.value,
  };
});

const resetBotpressSession = async () => {
  await window.botpress?.restartConversation();
};

const updateBotpressUser = async (bp: NonNullable<Window["botpress"]>) => {
  await bp.updateUser({ data: botpressUserData.value });
};

const parseCustomEvent = (event: unknown): BotpressCustomEvent | undefined => {
  if (event && typeof event === "object" && "action" in event) {
    return event as BotpressCustomEvent;
  }
  return undefined;
};

const isCalendlyUrl = (urlString: string): boolean => {
  try {
    const url = new URL(urlString);
    return url.protocol === "https:" && url.hostname.toLowerCase() === CALENDLY_HOSTNAME;
  } catch {
    return false;
  }
};

// Captured once, as early as this component sets up (well before any other component's
// mount-time router.push/replace can run). Some pages (e.g. AlgoliaResults, which powers
// /results/programs) rebuild the URL query from a filter whitelist on mount and silently
// drop any param not on that list. We're only supporting direct/external links
// (no in-app navigation), so this initial snapshot is the only value that matters.
const initialSearch = window.location.search;

const hasAutoOpenFlag = (): boolean => {
  try {
    return new URLSearchParams(initialSearch).get(ATLO_URL_FLAG.PARAM) === ATLO_URL_FLAG.VALUE;
  } catch (error) {
    datadogLogs.logger.error("Failed to read Atlo auto-open URL flag", { error });
    return false;
  }
};

const openChatIfFlagged = async () => {
  if (hasAutoOpenFlag()) {
    await window.botpress?.open();
  }
};

const setupBotpressEventHooks = (bp: NonNullable<Window["botpress"]>) => {
  try {
    bp.on("webchat:initialized", async () => {
      await updateBotpressUser(bp);
      await openChatIfFlagged();
    });

    bp.on("webchat:ready", async () => {
      await checkAuthTransition();
    });

    bp.on("webchat:opened", () => {
      isWidgetOpen.value = true;
      if (hasGreetedCurrentConversation.value) return;
      markConversationGreeted();
      void bp.sendEvent({ action: BOTPRESS_CLIENT_EVENTS.CONVERSATION_START });
    });

    bp.on("webchat:closed", () => {
      isWidgetOpen.value = false;
    });

    bp.on("conversation", () => {
      if (isWidgetOpen.value) {
        markConversationGreeted();
        void bp.sendEvent({ action: BOTPRESS_CLIENT_EVENTS.CONVERSATION_START });
      } else {
        clearConversationGreeted();
      }
    });

    bp.on("message", () => {
      setTimeout(() => {
        const shadow = document.querySelector(BOTPRESS_FAB_ROOT_SELECTOR)?.shadowRoot;
        if (!shadow) return;

        const allMessages = shadow.querySelectorAll<HTMLElement>(".bpMessageContainer");
        const lastMessage = allMessages[allMessages.length - 1];
        if (!lastMessage || lastMessage.getAttribute("data-direction") !== "incoming") return;

        const scrollContainer = shadow.querySelector<HTMLElement>(".bpMessageListContainer > div");
        if (!scrollContainer) return;

        const header = shadow.querySelector<HTMLElement>(".bpHeaderContainer");
        const headerHeight = header?.getBoundingClientRect().height ?? 0;

        const containerTop = scrollContainer.getBoundingClientRect().top;
        const msgTop = lastMessage.getBoundingClientRect().top;
        scrollContainer.scrollTo({
          top: msgTop - containerTop + scrollContainer.scrollTop - headerHeight,
          behavior: "smooth",
        });
      }, 450);
    });

    bp.on("customEvent", (event) => {
      const payload = parseCustomEvent(event);
      if (!payload) return;

      switch (payload.action) {
        case BOTPRESS_ACTIONS.SIGN_IN:
          window.location.href = "/sign-in";
          break;
        case BOTPRESS_ACTIONS.CREATE_ACCOUNT:
          window.location.href = "/create-account";
          break;
        case BOTPRESS_ACTIONS.SHOW_CALENDLY:
          if (!isCalendlyUrl(payload.url)) {
            datadogLogs.logger.warn("Blocked non-Calendly URL from Botpress.", {
              url: payload.url,
            });
          } else if (window.Calendly) {
            window.Calendly.showPopupWidget(payload.url);
          } else {
            window.open(payload.url, "_blank", "noopener,noreferrer");
          }
          break;
      }
    });
  } catch (error) {
    datadogLogs.logger.error("Error setting up Botpress webchat listeners:", { error });
  }
};

const initializeBotpress = async () => {
  const bp = window.botpress;
  if (!bp) {
    datadogLogs.logger.error("Botpress webchat script not loaded (window.botpress is missing).");
    return;
  }

  setupBotpressEventHooks(bp);

  bp.init({
    botId: import.meta.env.VITE_BOTPRESS_BOT_ID,
    configuration: {
      version: "v2",
      botName: " ",
      botAvatar: "https://files.bpcontent.cloud/2026/05/18/14/20260518140614-UPZHUDUM.png",
      website: {},
      email: {},
      phone: {},
      termsOfService: {},
      privacyPolicy: {},
      color: "#665eab",
      variant: "soft",
      headerVariant: "solid",
      themeMode: "light",
      fontFamily: "Montserrat",
      radius: 4,
      feedbackEnabled: false,
      footer: "Atlo is AI and can make mistakes.",
      soundEnabled: false,
      proactiveMessageEnabled: false,
      proactiveBubbleMessage: "Hi! 👋 Need help?",
      proactiveBubbleTriggerType: "afterDelay",
      proactiveBubbleDelayTime: 10,
      conversationHistory: false,
    },
    clientId: import.meta.env.VITE_BOTPRESS_CLIENT_ID,
  });
};

const setBotpressFabVisible = (visible: boolean) => {
  const fabRoot = document.querySelector<HTMLElement>(BOTPRESS_FAB_ROOT_SELECTOR);
  if (fabRoot) {
    fabRoot.style.display = visible ? "" : "none";
  }

  if (!visible) {
    window.botpress?.close();
  }
};

const syncBotpressVisibility = async () => {
  if (!shouldLoadBotpress.value) {
    if (botpressInitialized.value) {
      setBotpressFabVisible(false);
    }
    return;
  }

  if (!botpressInitialized.value) {
    botpressInitialized.value = true;
    await initializeBotpress();
    return;
  }

  setBotpressFabVisible(true);
};

onMounted(() => {
  void syncBotpressVisibility();
});

watch(shouldLoadBotpress, () => {
  void syncBotpressVisibility();
});
</script>
